<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'future', label: 'Futuros' },
  { value: 'completed', label: 'Concluidos' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'no_show', label: 'Faltas' },
];

const timeline = ref([]);
const recurrence = ref([]);
const recoveryAttempts = ref([]);
const relationship = ref(null);
const pagination = ref({ page: 1, pageSize: 8, total: 0, totalPages: 0 });
const services = ref([]);
const professionals = ref([]);
const establishments = ref([]);
const isLoading = ref(false);
const errorMessage = ref('');
const showFilters = ref(false);
const showManualForm = ref(false);
const selectedStatus = ref('all');
const filters = reactive({
  serviceId: '',
  professionalId: '',
  establishmentId: '',
  from: '',
  to: '',
});
const manualForm = reactive({
  occurredAt: '',
  serviceIds: [],
  professionalId: '',
  establishmentId: '',
  notes: '',
});

const hasTimeline = computed(() => timeline.value.length > 0);
const recoveryEmptyMessage = computed(() => {
  if (!relationship.value?.completedVisits) {
    return 'A recuperacao comeca apos o primeiro atendimento concluido.';
  }
  if (!relationship.value?.trackedServices) {
    return 'Nenhum servico deste cliente possui recorrencia configurada.';
  }
  if (relationship.value?.nextAppointment) {
    return 'Sem recuperacao pendente: existe um agendamento futuro.';
  }
  return 'Nenhuma tentativa de recuperacao foi criada para este contato.';
});

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatMoney(value, currency = 'EUR') {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency,
  }).format(Number(value || 0) / 100);
}

function serviceLabel(event) {
  if (event.services?.length) {
    return event.services.map(item => item.service_name).join(', ');
  }
  return (event.serviceType || 'Atendimento').replaceAll('_', ' ');
}

function eventTitle(event) {
  return event.kind === 'service_visit' ? 'Atendimento realizado' : 'Agendamento';
}

function statusLabel(status) {
  const labels = {
    pending_confirmation: 'Pendente',
    confirmed: 'Confirmado',
    rescheduled: 'Reagendado',
    completed: 'Concluido',
    cancelled: 'Cancelado',
    no_show: 'Falta',
  };
  return labels[status] || status;
}

function statusClass(status) {
  const classes = {
    pending_confirmation: 'bg-n-amber-3 text-n-amber-11',
    confirmed: 'bg-n-teal-3 text-n-teal-11',
    rescheduled: 'bg-n-blue-3 text-n-blue-11',
    completed: 'bg-n-teal-3 text-n-teal-11',
    cancelled: 'bg-n-ruby-3 text-n-ruby-11',
    no_show: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[status] || 'bg-n-slate-3 text-n-slate-11';
}

function retentionStatusLabel(status) {
  const labels = {
    tracking: 'Em acompanhamento',
    upcoming: 'Retorno proximo',
    overdue: 'Atrasado',
    scheduled: 'Agendado',
    blocked: 'Bloqueado',
    paused: 'Pausado',
    opted_out: 'Sem contato',
    not_configured: 'Sem regra',
  };
  return labels[status] || status;
}

function recoveryStatusLabel(status) {
  const labels = {
    pending: 'Programada',
    processing: 'Enviando',
    sent: 'Enviada',
    failed: 'Nova tentativa',
    cancelled: 'Cancelada',
    skipped: 'Ignorada',
  };
  return labels[status] || status;
}

function resetManualForm() {
  Object.assign(manualForm, {
    occurredAt: '',
    serviceIds: [],
    professionalId: '',
    establishmentId: '',
    notes: '',
  });
}

async function fetchLookups() {
  try {
    const [servicesResponse, professionalsResponse, establishmentsResponse] =
      await Promise.all([
        fetch('/api/superwoot/services'),
        fetch('/api/superwoot/professionals'),
        fetch('/api/superwoot/establishments'),
      ]);
    if (
      !servicesResponse.ok ||
      !professionalsResponse.ok ||
      !establishmentsResponse.ok
    ) {
      throw new Error('request_failed');
    }
    const [servicesPayload, professionalsPayload, establishmentsPayload] =
      await Promise.all([
        servicesResponse.json(),
        professionalsResponse.json(),
        establishmentsResponse.json(),
      ]);
    services.value = servicesPayload.services || [];
    professionals.value = professionalsPayload.professionals || [];
    establishments.value = establishmentsPayload.establishments || [];
  } catch {
    errorMessage.value = 'Nao foi possivel carregar os filtros.';
  }
}

async function fetchHistory(page = 1) {
  isLoading.value = true;
  errorMessage.value = '';
  const params = new URLSearchParams({
    contactId: String(props.contactId),
    page: String(page),
    pageSize: String(pagination.value.pageSize),
    status: selectedStatus.value,
  });
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  try {
    const response = await fetch(
      `/api/superwoot/contact-service-history?${params}`
    );
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    relationship.value = payload.relationship;
    timeline.value = payload.timeline || [];
    recurrence.value = payload.recurrence || [];
    recoveryAttempts.value = payload.recoveryAttempts || [];
    pagination.value = payload.pagination;
  } catch {
    timeline.value = [];
    recurrence.value = [];
    recoveryAttempts.value = [];
    relationship.value = null;
    errorMessage.value = 'Nao foi possivel carregar os atendimentos.';
  } finally {
    isLoading.value = false;
  }
}

async function saveManualHistory() {
  if (!manualForm.occurredAt || manualForm.serviceIds.length === 0) {
    errorMessage.value = 'Informe a data e ao menos um servico.';
    return;
  }

  try {
    const response = await fetch('/api/superwoot/contact-service-history', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contactId: String(props.contactId),
        ...manualForm,
        professionalId: manualForm.professionalId || null,
        establishmentId: manualForm.establishmentId || null,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    resetManualForm();
    showManualForm.value = false;
    await fetchHistory(1);
  } catch {
    errorMessage.value = 'Nao foi possivel registrar o atendimento.';
  }
}

watch(selectedStatus, () => fetchHistory(1));
watch(() => props.contactId, () => fetchHistory(1));
onMounted(async () => {
  await Promise.all([fetchLookups(), fetchHistory()]);
});
</script>

<template>
  <div class="flex flex-col gap-4 px-6 py-5">
    <div class="flex items-center justify-between gap-2">
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">Atendimentos</h3>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ pagination.total }} registros
        </p>
      </div>
      <div class="flex gap-1">
        <Button
          icon="i-lucide-filter"
          size="sm"
          slate
          @click="showFilters = !showFilters"
        />
        <Button
          icon="i-lucide-plus"
          size="sm"
          @click="showManualForm = !showManualForm"
        />
      </div>
    </div>

    <div class="flex flex-wrap gap-1">
      <button
        v-for="option in STATUS_OPTIONS"
        :key="option.value"
        type="button"
        class="px-2 py-1 text-xs border rounded-md border-n-weak"
        :class="
          selectedStatus === option.value
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-solid-2 text-n-slate-11'
        "
        @click="selectedStatus = option.value"
      >
        {{ option.label }}
      </button>
    </div>

    <form
      v-if="showFilters"
      class="grid grid-cols-1 gap-2 p-3 border rounded-md border-n-weak bg-n-solid-2"
      @submit.prevent="fetchHistory(1)"
    >
      <select v-model="filters.serviceId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
        <option value="">Todos os servicos</option>
        <option v-for="service in services" :key="service.id" :value="service.id">
          {{ service.name }}
        </option>
      </select>
      <select v-model="filters.professionalId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
        <option value="">Todos os profissionais</option>
        <option v-for="professional in professionals" :key="professional.id" :value="professional.id">
          {{ professional.name }}
        </option>
      </select>
      <select v-model="filters.establishmentId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
        <option value="">Todas as unidades</option>
        <option v-for="establishment in establishments" :key="establishment.id" :value="establishment.id">
          {{ establishment.name }}
        </option>
      </select>
      <div class="grid grid-cols-2 gap-2">
        <input v-model="filters.from" type="date" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
        <input v-model="filters.to" type="date" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
      </div>
      <Button label="Aplicar filtros" size="sm" type="submit" />
    </form>

    <form
      v-if="showManualForm"
      class="grid grid-cols-1 gap-2 p-3 border rounded-md border-n-weak bg-n-solid-2"
      @submit.prevent="saveManualHistory"
    >
      <h4 class="text-xs font-medium text-n-slate-12">Registrar historico anterior</h4>
      <input v-model="manualForm.occurredAt" type="datetime-local" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
      <label v-for="service in services" :key="service.id" class="flex items-center gap-2 text-xs text-n-slate-11">
        <input v-model="manualForm.serviceIds" type="checkbox" :value="service.id" />
        {{ service.name }}
      </label>
      <select v-model="manualForm.professionalId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
        <option value="">Sem profissional informado</option>
        <option v-for="professional in professionals" :key="professional.id" :value="professional.id">
          {{ professional.name }}
        </option>
      </select>
      <select v-model="manualForm.establishmentId" class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12">
        <option value="">Sem unidade informada</option>
        <option v-for="establishment in establishments" :key="establishment.id" :value="establishment.id">
          {{ establishment.name }}
        </option>
      </select>
      <textarea v-model="manualForm.notes" rows="2" placeholder="Observacoes" class="px-2 py-1 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12" />
      <Button label="Salvar atendimento" size="sm" type="submit" />
    </form>

    <p v-if="errorMessage" class="text-xs text-n-ruby-11">
      {{ errorMessage }}
    </p>

    <section class="flex flex-col gap-2">
      <h4 class="text-xs font-medium uppercase text-n-slate-10">Recorrencia</h4>
      <div
        v-for="item in recurrence"
        :key="item.serviceId"
        class="p-3 border rounded-md border-n-weak bg-n-solid-2"
      >
        <strong class="block text-xs font-medium text-n-slate-12">
          {{ item.serviceName }}
        </strong>
        <span class="block mt-1 text-xs text-n-slate-10">
          Ultimo atendimento ha {{ item.daysSinceLastVisit }} dias
        </span>
        <span class="block mt-1 text-xs text-n-slate-10">
          {{ item.totalVisits }} realizados ·
          {{ item.totalDurationMinutes }} min ·
          {{ formatMoney(item.totalSpentCents, item.currency) }}
        </span>
        <span class="block mt-1 text-xs text-n-slate-10">
          {{ retentionStatusLabel(item.status) }}
          <template v-if="item.nextDueAt">
            · retorno em {{ formatDate(item.nextDueAt) }}
          </template>
        </span>
        <span class="block mt-1 text-xs text-n-slate-10">
          {{ item.nextAction }}
        </span>
      </div>
      <p v-if="!recurrence.length" class="p-3 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-10">
        Nenhum ciclo de recorrencia registrado.
      </p>
    </section>

    <section class="flex flex-col gap-2">
      <h4 class="text-xs font-medium uppercase text-n-slate-10">
        Recuperacao
      </h4>
      <ol class="flex flex-col gap-2">
        <li
          v-for="attempt in recoveryAttempts"
          :key="attempt.id"
          class="p-3 border rounded-md border-n-weak bg-n-solid-2"
        >
          <div class="flex items-start justify-between gap-2">
            <strong class="text-xs font-medium text-n-slate-12">
              {{ attempt.services?.name || 'Atendimento' }}
            </strong>
            <span class="px-2 py-0.5 text-xs rounded bg-n-slate-3 text-n-slate-11">
              {{ recoveryStatusLabel(attempt.status) }}
            </span>
          </div>
          <span class="block mt-1 text-xs text-n-slate-10">
            {{ attempt.stage }} · {{ formatDate(attempt.sent_at || attempt.scheduled_for) }}
          </span>
          <span v-if="attempt.last_error" class="block mt-1 text-xs text-n-ruby-11">
            {{ attempt.last_error }}
          </span>
        </li>
      </ol>
      <p v-if="!recoveryAttempts.length" class="p-3 text-xs border rounded-md border-n-weak bg-n-solid-2 text-n-slate-10">
        {{ recoveryEmptyMessage }}
      </p>
    </section>

    <div v-if="isLoading" class="text-sm text-n-slate-10">
      Carregando atendimentos...
    </div>
    <div v-else-if="!hasTimeline" class="text-sm text-n-slate-10">
      Nenhum atendimento encontrado.
    </div>
    <ol v-else class="flex flex-col gap-3">
      <li
        v-for="event in timeline"
        :key="`${event.kind}-${event.id}`"
        class="p-3 border rounded-md border-n-weak bg-n-solid-2"
      >
        <div class="flex items-start justify-between gap-2">
          <strong class="text-xs font-medium text-n-slate-12">
            {{ eventTitle(event) }}
          </strong>
          <span class="px-2 py-0.5 text-xs rounded" :class="statusClass(event.status)">
            {{ statusLabel(event.status) }}
          </span>
        </div>
        <span class="block mt-2 text-xs text-n-slate-10">
          {{ formatDate(event.occurredAt) }}
        </span>
        <span class="block mt-1 text-sm text-n-slate-12">
          {{ serviceLabel(event) }}
        </span>
        <span v-if="event.professional?.name" class="block mt-1 text-xs text-n-slate-10">
          {{ event.professional.name }}
        </span>
        <span v-if="event.establishment?.name" class="block mt-1 text-xs text-n-slate-10">
          {{ event.establishment.name }}
        </span>
        <span v-if="event.services?.length" class="block mt-1 text-xs text-n-slate-10">
          {{ event.services.reduce((total, item) => total + Number(item.duration_minutes || 0) * Number(item.quantity || 1), 0) }} min ·
          {{ formatMoney(event.services.reduce((total, item) => total + Number(item.price_cents || 0) * Number(item.quantity || 1), 0), event.services[0]?.currency) }}
        </span>
        <span v-if="event.origin === 'manual'" class="block mt-2 text-xs text-n-amber-11">
          Historico manual
        </span>
      </li>
    </ol>

    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between gap-2">
      <Button
        icon="i-lucide-chevron-left"
        size="sm"
        slate
        :disabled="pagination.page <= 1"
        @click="fetchHistory(pagination.page - 1)"
      />
      <span class="text-xs text-n-slate-10">
        {{ pagination.page }} / {{ pagination.totalPages }}
      </span>
      <Button
        icon="i-lucide-chevron-right"
        size="sm"
        slate
        :disabled="pagination.page >= pagination.totalPages"
        @click="fetchHistory(pagination.page + 1)"
      />
    </div>
  </div>
</template>
